import React, { Fragment, useState, FC, useEffect } from "react";
import { Transition } from "@headlessui/react";
import DatePicker from "react-datepicker";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import useOutsideAlerter from "hooks/useOutsideAlerter";
import { Formatter } from "utils/formatters";
import { weekdays } from "utils/weekdays";

import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { useMetadataUser } from "contexts/metadata-user-context";
import { CalendarIcon } from "@heroicons/react/24/outline";

export interface StayDatesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  autoFocus?: boolean;
}

const StayDatesRangeInput: FC<StayDatesRangeInputProps> = ({
  className = "[ lg:nc-flex-2 ]",
  fieldClassName = "[ nc-hero-field-padding--small ]",
  autoFocus = false,
}) => {
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();
  const refContainer = React.createRef<HTMLDivElement>();
  const [isOpen, setIsOpen] = useState(autoFocus);
  const { profile } = useMetadataUser();

  useOutsideAlerter(refContainer, () => setIsOpen(false));

  const {
    numNights,
    setCheckInDate,
    checkInDate,
    setNumNights,
    showHeroSearch,
    setShowHeroSearch,
  } = useHotels();

  useEffect(() => {
    if (checkInDate && numNights) {
      const checkInDateTypeDate = new Date(`${checkInDate}T00:00:00.000`);
      const checkoutDate = new Date(checkInDateTypeDate);
      checkoutDate.setDate(checkInDateTypeDate.getDate() + numNights);
      if (profile?.subscription?.active && profile?.subscription?.blocked_until && !profile?.company) {
        if (checkoutDate < new Date(profile.subscription.blocked_until)) {
          const newCheckinDate = new Date(profile.subscription.blocked_until);
          newCheckinDate.setDate(newCheckinDate.getDate() + 2);
          setStartDate(newCheckinDate);

          const newCheckoutDate = new Date(newCheckinDate);
          newCheckoutDate.setDate(newCheckoutDate.getDate() + numNights);
          setEndDate(newCheckoutDate);

          setCheckInDate(Formatter.prettyDateHifen(newCheckinDate));
        }
      }
      setStartDate(checkInDateTypeDate);
      setEndDate(checkoutDate);
    }

    if (showHeroSearch === "dates") {
      setIsOpen(true);
    } else if (showHeroSearch === "guests") {
      setIsOpen(false);
    }
  }, [showHeroSearch]);

  const dateBlocked = (date: Date): Date => {
    if (profile?.subscription?.active && profile?.subscription?.blocked_until && !profile?.company) {
      if (date < new Date(profile.subscription.blocked_until)) {
        const newDate = new Date(profile.subscription.blocked_until);
        newDate.setDate(newDate.getDate() + 2);
        return newDate;
      }
    }
    return date;
  }

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  
    if (start && end) {
      const startDateFormated = Formatter.prettyDateHifen(start);
      const rangeInMilliseconds = end.getTime() - start.getTime();
      const rangeInDays = rangeInMilliseconds / (1000 * 60 * 60 * 24);
      setCheckInDate(startDateFormated);
      setNumNights(rangeInDays);
      setShowHeroSearch("guests");
      setIsOpen(false);
    }
  };
  

  const renderInput = () => {
    return (
      <div className="flex-grow flex items-center gap-2 text-left">
        <div className="text-neutral-500 dark:text-neutral-400">
          <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div>
          <span className="block xl:text-base font-semibold">
            {startDate
              ?.toLocaleDateString("pt-BR", {
                month: "short",
                day: "2-digit",
              })
              .replace(".", "") || "Adicionar datas"}
            {endDate
              ? " - " +
              endDate
                ?.toLocaleDateString("pt-BR", {
                  month: "short",
                  day: "2-digit",
                })
                .replace(".", "")
              : ""}
          </span>
          <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
            {"Check in - Check out"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={refContainer}
      className={`StayDatesRangeInput z-30 relative flex ${className}`}
    >
      <div
        className={`flex-1 z-50 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none ${isOpen ? "nc-hero-field-focused--2" : ""
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {renderInput()}
        {startDate && isOpen && (
          <ClearDataButton onClick={() => onChangeDate([null, null])} />
        )}
      </div>

      {isOpen && (
        <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -inset-x-0.5 bg-white dark:bg-neutral-800"></div>
      )}

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
        show={isOpen}
      >
        <div className="absolute left-1/2 z-30 mt-3 top-full w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-3xl">
          <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
            <DatePicker
              formatWeekDay={(nameOfDay) =>
                weekdays[weekdays.findIndex((day) => day.enUS === nameOfDay)]
                  .ptBR
              }
              selected={startDate}
              onChange={onChangeDate}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              minDate={dateBlocked(new Date())}
              monthsShown={2}
              showPopperArrow={false}
              inline
              renderCustomHeader={(p) => (
                <DatePickerCustomHeaderTwoMonth {...p} />
              )}
              renderDayContents={(day, date) => (
                <DatePickerCustomDay dayOfMonth={day} date={date} />
              )}
            />
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default StayDatesRangeInput;
